import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import NotificationDropdown from '../components/NotificationDropdown';
import { API_URL, SERVER_URL } from '../config/env';
import { 
  Home, 
  FileText, 
  Users, 
  Folder, 
  Building, 
  BarChart3, 
  Settings, 
  Menu, 
  X,
  LogOut,
  User,
  PlusCircle,
} from 'lucide-react';
import AdminAddGrievanceModal from '../components/AdminAddGrievanceModal';


interface SiteSettings {
  logoUrl?: string | null;
}

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddGrievance, setShowAddGrievance] = useState(false);
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();

  const navigation = [
    { name: t('nav.dashboard'), href: '/super-admin/dashboard', icon: Home },
    { name: t('nav.grievances'), href: '/super-admin/grievances', icon: FileText },
    { name: 'Reviews', href: '/super-admin/reviews', icon: BarChart3 },
    { name: t('nav.users'), href: '/super-admin/users', icon: Users },
    { name: t('nav.categories'), href: '/super-admin/categories', icon: Folder },
    { name: t('nav.departments'), href: '/super-admin/departments', icon: Building },
    { name: t('nav.reports'), href: '/super-admin/reports', icon: BarChart3 },
    { name: t('nav.settings'), href: '/super-admin/sitesettings', icon: Settings },
  ];

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ne' : 'en');
  };

  //site setting register
    const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
    useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/settings`);
        const data = await res.json();
        setSiteSettings(data);
      } catch (err) {
        console.error(err);
      }
    };
    loadSettings();
  }, []);
  

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <div className="flex items-center space-x-2">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center bg-white">
              {siteSettings?.logoUrl ? (
                <img
                  src={`${SERVER_URL}${siteSettings.logoUrl}`}
                  alt="Ministry Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-white font-bold text-sm">IGMS</span>
              )}
            </div>
            <span className="text-lg font-semibold text-gray-900"> Super Admin</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-8 px-4">
          <div className="space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User info at bottom */}
        <Link
          to="/super-admin/profile"
          className="absolute bottom-0 left-0 right-0 p-4 border-t bg-gray-50 block"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </Link>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity duration-300 ease-in-out md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="md:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-4 ml-auto">
              {/* Add Grievance Button */}
              <button
                onClick={() => setShowAddGrievance(true)}
                title="Add Grievance Manually"
                className="flex items-center gap-1.5 text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <PlusCircle className="h-5 w-5" />
                <span className="hidden sm:inline">Add Grievance</span>
              </button>

              {/* Notifications - Only for Super Admin */}
              {(user?.role === 'super_admin' || user?.roles?.some(role => role.name.toLowerCase() === 'super_admin')) && (
                <div className="relative">
                  <NotificationDropdown />
                </div>
              )}
              
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {language === 'en' ? 'नेपाली' : 'English'}
              </button>

              {/* Logout */}
              <button
                onClick={logout}
                className="flex items-center space-x-2 text-gray-700 hover:text-red-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Add Grievance Modal */}
      {showAddGrievance && (
        <AdminAddGrievanceModal onClose={() => setShowAddGrievance(false)} />
      )}
    </div>
  );
};

export default AdminLayout;
